import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Autocomplete,
    ListItemIcon
} from '@mui/material';
import { ExpandMore, Star, Close, Check } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { debounce } from 'lodash';

import MainCard from 'ui-component/cards/MainCard';
import Loader from 'ui-component/Loader';
import { StyledBox, Header, H3, CancelButtonStyle, ButtonStyle } from './ControlsStyle';
import { boxProps, selectProps, datetimeProps, numberProps } from './GlobalFilter';
import { getObjects, getAssignmentObjects, getAssignmentFilters, getContainerFilters } from 'services/SearchService';
import { ContainerObject } from 'types/container';
import { Filter, RequestParams } from 'types/pagination';
import { TagData } from 'types/tag';
import { IconEye } from '@tabler/icons';
import GlobalDialog from './GlobalDialog';
import Preview from './Preview';

export interface TypeCheckbox extends TagData {
    checked?: boolean;
}

export interface ObjectSelectProps {
    onClose?: () => void;
    setObjects?: (val: ContainerObject[]) => void;
    existingObjects: ContainerObject[];
    setAssignmentObjects?: (val: ContainerObject[]) => void;
    restrictToType?: number;
    restrictToProducts?: string[] | number[];
    restrictToProductCategory?: number;
}

const ObjectSelect = (props: ObjectSelectProps) => {
    const { restrictToProductCategory, restrictToType, onClose, setObjects, setAssignmentObjects, existingObjects } = props;

    const [searchText, setSearchText] = useState<string>();
    const [results, setResults] = useState<ContainerObject[]>([]);
    const [resultsList, setResultsList] = useState<ContainerObject[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [openPreview, setOpenPreview] = useState<boolean>(false);
    const intl = useIntl();
    const [boxSection, setBoxSection] = useState<boxProps>();
    const [selectSection, setSelectSection] = useState<selectProps>();
    const [datetimeSection, setDatetimeSection] = useState<datetimeProps>();
    const [numberSection, setNumberSection] = useState<numberProps>();
    const [currentId, setCurrentId] = useState<number>();
    const [currentType, setCurrentType] = useState<string>('');
    const [newFilters, setNewFilters] = useState<Filter[]>(
        restrictToProductCategory || restrictToProductCategory === 0
            ? [{ name: 'product_category_id', value: restrictToProductCategory.toString() }]
            : []
    );

    const [requestParams, setRequestParams] = useState<RequestParams>({
        page: 1,
        per_page: 10,
        sort: [{ field: 'active', sort: 'desc' }],
        filters: []
    });

    useEffect(() => {
        if (setAssignmentObjects || restrictToType == 10 || restrictToType === 11) {
            getAssignmentFilters(restrictToType, restrictToProductCategory)
                .then((filters: []) => fillSections(filters))
                .then(() => setIsLoading(false));
        } else if (setObjects && restrictToProductCategory) {
            getContainerFilters(restrictToType, restrictToProductCategory)
                .then((filters: []) => fillSections(filters))
                .then(() => setIsLoading(false));
        }
    }, [setAssignmentObjects, setObjects, restrictToType, restrictToProductCategory]);

    useEffect(() => {
        setIsLoading(true);

        if (setAssignmentObjects || restrictToType == 10 || restrictToType === 11) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            getAssignmentObjects(setResults, requestParams).then(() => setIsLoading(false));
        } else {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            getObjects(setResults, requestParams).then(() => setIsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestParams, setAssignmentObjects]);

    useEffect(() => {
        setRequestParams((prevRequestParams: any) => ({
            ...prevRequestParams,
            filters: newFilters
        }));
    }, [newFilters]);

    useEffect(() => {
        setNewFilters((prev) => {
            const updatedFilters = prev.filter((f) => f.name !== 'search') || [];

            if (searchText !== undefined && Number(searchText) !== 0) {
                updatedFilters.push({ name: 'search', value: searchText });
            }

            return updatedFilters;
        });
    }, [searchText]);

    useEffect(() => {
        const newItem = existingObjects;
        setResultsList(newItem);
    }, [existingObjects]);

    useEffect(() => {
        // We use a map object to handle the name of the id field for each type

        let typeMap: any = {
            question: 'question_id',
            collection: 'collection_id',
            unit: 'unit_id',
            container: 'container_id'
        };

        existingObjects.forEach((obj: any) => {
            if (obj.type_id === 10 || obj.type_id === 11) {
                typeMap = {
                    question: 'question_id',
                    collection: 'collection_id',
                    unit: 'unit_id',
                    container: 'selected_container_id'
                };
            } else {
                typeMap = {
                    question: 'question_id',
                    collection: 'collection_id',
                    unit: 'unit_id',
                    container: 'container_id'
                };
            }
        });

        // Creating a map to store existing objects by their unique identifier for quick access
        const existingMap = new Map();
        existingObjects.forEach((obj: any) => {
            const key = typeMap[obj.type];
            existingMap.set(obj[key], obj);
        });

        const mergedResults = results.map((item: any) => {
            const key = typeMap[item.type];
            if (existingMap.has(item[key])) {
                // Mark as checked if exists in existingObjects
                return { ...item, checked: true };
            }
            return item;
        });

        // Add items from existingObjects that are not in results
        existingObjects.forEach((obj: any) => {
            const key = typeMap[obj.type];

            if (!mergedResults.some((item) => item[key] === obj[key])) {
                mergedResults.push({ ...obj, checked: true }); // Assuming you want them marked as checked
            }
        });

        setResultsList(mergedResults);
    }, [results, existingObjects]);

    const fillSections = async (filters: []) => {
        filters.forEach((element: any) => {
            const data: any = element.items;
            switch (element.title) {
                case 'select':
                    setSelectSection(data);
                    break;
                case 'box':
                    setBoxSection(data);
                    break;
                case 'datetime':
                    setDatetimeSection(data);
                    break;
                case 'number':
                    setNumberSection(data);
                    break;
            }
        });
    };

    const handleClose = () => {
        const selectedObjects = resultsList.filter((item) => item.checked);

        selectedObjects.forEach((item, index) => {
            item.priority = index + 1;
            item.id = uuidv4();
        });

        setObjects && setObjects(selectedObjects);
        setAssignmentObjects && setAssignmentObjects(selectedObjects);

        onClose && onClose();
    };

    const debouncedSearchTextChange = useMemo(() => debounce(setSearchText, 300), []);

    const handleToggle = (index: number) => () => {
        const newChecked = [...resultsList];
        newChecked[index].checked = !newChecked[index].checked;
        setResultsList(newChecked);
    };

    function handleOnChange(e: any, item: any) {
        const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
        // adding a new value to the existing filter
        if (e.target.checked) {
            const filterToUpdate = newFilters.find((obj) => obj.name === item.name);
            // if the filter exists, we need to get the current value else is empty
            let currentValue = [];
            if (filterToUpdate) {
                currentValue = filterToUpdate.name === 'examName' ? filterToUpdate.value : JSON.parse(filterToUpdate.value);
            }
            // if the filter is examName, we need to add the questionType filter also
            if (item.name === 'examName') {
                setNewFilters([
                    ...removedKeyFilter,
                    { name: item.name, value: e.target.value },
                    { name: 'questionType', value: JSON.stringify([12]) }
                ]);
                return;
            }
            setNewFilters([...removedKeyFilter, { name: item.name, value: JSON.stringify([...currentValue, Number(e.target.value)]) }]);
        } else {
            // remove value from filter
            const filterToUpdate = newFilters.find((obj) => obj.name === item.name);
            if (filterToUpdate) {
                if (filterToUpdate.name === 'examName') {
                    const clearFilters = newFilters.filter((obj) => obj.name !== 'examName' && obj.name !== 'questionType');
                    setNewFilters([...clearFilters]);
                    return;
                }
                const currentValue = JSON.parse(filterToUpdate.value);
                const newValue = currentValue.filter((val: any) => val !== Number(e.target.value));
                // if the filter is empty, we need to remove it from the list
                if (newValue.length === 0) {
                    setNewFilters([...removedKeyFilter]);
                    return;
                }
                setNewFilters([...removedKeyFilter, { ...filterToUpdate, value: JSON.stringify(newValue) }]);
            }
        }
    }

    function handleTagChange(type: string, event: React.ChangeEvent<{}>, newValue: { id: string; name: string }[] | []) {
        const ids = newValue.map((tag) => tag.id);

        const removedKeyFilter = newFilters.filter((obj) => obj.name !== type);
        if (ids.length === 0) {
            setNewFilters([...removedKeyFilter]);
            return;
        }
        setNewFilters([...removedKeyFilter, { name: type, value: ids.join() }]);
    }

    const GenericDisplayName = ({ object }: { object: ContainerObject }) => {
        const {
            display_name,
            type,
            container_id,
            question_id,
            collection_id,
            unit_id,
            type_name,
            selected_container,
            selected_container_id
        } = object;

        // Ensure type is always a string
        const typeName = typeof type === 'object' ? 'question' : type;

        const objectId = display_name
            ? ''
            : {
                  container: selected_container_id || container_id,
                  question: question_id,
                  collection: collection_id,
                  unit: unit_id
              }[typeName];

        const displayName = selected_container ? selected_container.display_name : display_name || type_name;

        return (
            <>
                <FormattedMessage id={displayName} defaultMessage={displayName} /> {`${objectId}`}
            </>
        );
    };

    const PreviewButton = (item: ContainerObject) => {
        const { type, question_id, collection_id } = item;
        const typeName = typeof type === 'object' ? 'question' : type;
        if (!['question', 'collection'].includes(typeName)) {
            return null;
        }

        return (
            <IconButton
                onClick={(e) => {
                    setCurrentId(question_id ?? collection_id);
                    setCurrentType(type);
                    setOpenPreview(true);
                }}
            >
                <IconEye />
            </IconButton>
        );
    };

    return (
        <>
            {isLoading && <Loader />}
            <Box sx={{ width: '70vw', height: '90vh', maxWidth: '1000px', maxHeight: '725px', padding: 3, overflow: 'hidden' }}>
                <Header>
                    <H3 variant="h3" marginLeft="0px !important">
                        <FormattedMessage id="search-filter" />
                    </H3>
                    <IconButton color="primary" sx={ModalCloseButton} onClick={onClose}>
                        <Close />
                    </IconButton>
                </Header>
                <Grid container>
                    <Grid container item justifyContent="center" spacing={5} sx={{ margin: '10px 0px 15px 0px' }}>
                        <Grid item xs={6} sx={{ paddingLeft: '0px !important', pt: '0px !important' }} pt="0px !important">
                            <MainCard
                                sx={{
                                    border: 'none',
                                    boxShadow: `0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)`,
                                    height: '65vh'
                                }}
                                contentSX={{ height: '100%' }}
                            >
                                <Box>
                                    <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 400, textAlign: 'center' }}>
                                        <FormattedMessage id="filtering" />
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        label
                                        sx={{ marginTop: '58px' }}
                                        onChange={(e) => debouncedSearchTextChange(e.target.value)}
                                    />
                                </Box>

                                <StyledBox>
                                    <Box>
                                        <Typography sx={{ marginLeft: 1, mb: 1.25, color: '#878E95' }}>
                                            <FormattedMessage id="label-search" />
                                        </Typography>

                                        {boxSection && boxSection?.options.filter((e) => e.values).length === boxSection?.options.length && (
                                            <>
                                                <Box mt="28px" mb="24px">
                                                    {boxSection?.options.map((item, index) => (
                                                        <Accordion key={index} sx={{ '&:before': { display: 'none' } }}>
                                                            <AccordionSummary
                                                                expandIcon={<ExpandMore />}
                                                                aria-controls={item.label}
                                                                id={`panel${index}-header`}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                    <Star />
                                                                    <Typography sx={{ flexGrow: 1, marginLeft: 1, color: '#222529' }}>
                                                                        <FormattedMessage id={item.label} values={{ indicator: '' }} />
                                                                    </Typography>
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <List>
                                                                    <ListItemComponent
                                                                        item={item}
                                                                        newFilters={newFilters}
                                                                        // eslint-disable-next-line react/jsx-no-bind
                                                                        handleOnChange={handleOnChange}
                                                                    />
                                                                </List>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    ))}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                    <Box mt={6.25}>
                                        {setObjects && numberSection && (
                                            <>
                                                {numberSection?.options
                                                    .filter((item) => item.name !== 'amountQuestions')
                                                    .map((item, index) => (
                                                        <Box key={`text-field-number-${index}`} sx={{ mb: 6.25 }}>
                                                            <Typography sx={{ marginLeft: 1, color: '#878E95' }}>
                                                                <FormattedMessage id={item.label} values={{ level: '' }} />
                                                            </Typography>

                                                            <TextField
                                                                type="number"
                                                                label={<FormattedMessage id="enter-value" />}
                                                                inputProps={{ min: item.min, max: item.max, step: item.step }}
                                                                sx={{ width: '10rem', marginTop: 1.25 }}
                                                                onChange={(e) => {
                                                                    const removedKeyFilter = newFilters.filter(
                                                                        (obj) => obj.name !== item.name
                                                                    );
                                                                    setNewFilters([
                                                                        ...removedKeyFilter,
                                                                        { name: item.name, value: e.target.value }
                                                                    ]);
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                            </>
                                        )}

                                        <Typography sx={{ marginLeft: 1, color: '#878E95' }}>
                                            <FormattedMessage id="labelforObject" />
                                        </Typography>

                                        {selectSection &&
                                            selectSection?.options.filter((e) => e.values).length === selectSection?.options.length && (
                                                <>
                                                    {setObjects ? (
                                                        <>
                                                            <Box mt="28px" mb="24px">
                                                                {selectSection?.options
                                                                    .filter((item) => item.name !== 'unitType')
                                                                    .map((item, index) => (
                                                                        <Autocomplete
                                                                            multiple
                                                                            fullWidth
                                                                            disableCloseOnSelect
                                                                            sx={{ mt: 2 }}
                                                                            key={`text-field-select${index}`}
                                                                            options={Object.keys(item.values).map(
                                                                                (key: string): TagData => ({
                                                                                    id: item.values[key],
                                                                                    name: key
                                                                                })
                                                                            )}
                                                                            getOptionLabel={(option) => option.name}
                                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                            renderOption={(optionProps, option, { selected }) => (
                                                                                <MenuItem
                                                                                    {...optionProps}
                                                                                    key={option.id}
                                                                                    value={option.id}
                                                                                    sx={{ justifyContent: 'space-between' }}
                                                                                >
                                                                                    {option.name}
                                                                                    {selected ? <Check color="primary" /> : null}
                                                                                </MenuItem>
                                                                            )}
                                                                            onChange={(event, newValue) =>
                                                                                handleTagChange(item.name, event, newValue)
                                                                            }
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    variant="outlined"
                                                                                    label={
                                                                                        <FormattedMessage
                                                                                            id={item.label}
                                                                                            values={{ indicator: '' }}
                                                                                        />
                                                                                    }
                                                                                />
                                                                            )}
                                                                        />
                                                                    ))}
                                                            </Box>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Box mt="28px" mb="24px">
                                                                {selectSection?.options.map((item, index) => (
                                                                    <Autocomplete
                                                                        multiple
                                                                        fullWidth
                                                                        disableCloseOnSelect
                                                                        sx={{ mt: 2 }}
                                                                        key={`text-field-select${index}`}
                                                                        options={Object.keys(item.values).map(
                                                                            (key: string): TagData => ({
                                                                                id: item.values[key],
                                                                                name: key
                                                                            })
                                                                        )}
                                                                        getOptionLabel={(option) => option.name}
                                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                        renderOption={(optionProps, option, { selected }) => (
                                                                            <MenuItem
                                                                                {...optionProps}
                                                                                key={option.id}
                                                                                value={option.id}
                                                                                sx={{ justifyContent: 'space-between' }}
                                                                            >
                                                                                {option.name}
                                                                                {selected ? <Check color="primary" /> : null}
                                                                            </MenuItem>
                                                                        )}
                                                                        onChange={(event, newValue) =>
                                                                            handleTagChange(item.name, event, newValue)
                                                                        }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                variant="outlined"
                                                                                label={
                                                                                    <FormattedMessage
                                                                                        id={item.label}
                                                                                        values={{ indicator: '' }}
                                                                                    />
                                                                                }
                                                                            />
                                                                        )}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </>
                                                    )}
                                                </>
                                            )}

                                        <Typography sx={{ marginLeft: 1, mt: 2.5, color: '#878E95' }}>
                                            <FormattedMessage id="creation-date" />
                                        </Typography>

                                        {datetimeSection && (
                                            <>
                                                <Box mt="28px" mb="23px">
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        {datetimeSection?.options.map((item, index) => (
                                                            <Box sx={{ mt: 2 }} key={`date-field-${index}`}>
                                                                <DatePicker
                                                                    label={<FormattedMessage id={item.label} />}
                                                                    inputFormat="dd-MM-yyyy"
                                                                    value={newFilters.find((f) => f.name === item.name)?.value || null}
                                                                    onChange={(date) => {
                                                                        if (date) {
                                                                            const removedKeyFilter = newFilters.filter(
                                                                                (obj) => obj.name !== item.name
                                                                            );
                                                                            setNewFilters([
                                                                                ...removedKeyFilter,
                                                                                {
                                                                                    name: item.name,
                                                                                    value: format(new Date(date), 'yyyy-MM-dd')
                                                                                }
                                                                            ]);
                                                                        }
                                                                    }}
                                                                    renderInput={(params) => <TextField {...params} />}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </LocalizationProvider>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </StyledBox>
                            </MainCard>
                        </Grid>
                        <Grid item xs={6} pt="0px !important">
                            <MainCard
                                sx={{
                                    border: 'none',
                                    boxShadow: `0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)`,
                                    height: '65vh'
                                }}
                            >
                                <Typography>
                                    <FormattedMessage id="search-result" />
                                </Typography>
                                <StyledBox sx={{ height: '45vh' }}>
                                    <List>
                                        {resultsList.map((item, index) => (
                                            <ListItem key={index}>
                                                <Checkbox checked={item.checked ?? false} onClick={handleToggle(index)} />
                                                <ListItemText
                                                    primary={<GenericDisplayName object={item} />}
                                                    secondary={<DisplayMatches item={item} term={searchText} />}
                                                />
                                                <ListItemIcon>{PreviewButton(item)}</ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </StyledBox>
                            </MainCard>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', position: 'absolute', bottom: '30px', right: '25px' }}>
                        <CancelButtonStyle sx={{ mr: '20px', width: '110px' }} onClick={onClose} variant="contained">
                            <FormattedMessage id="cancel" />
                        </CancelButtonStyle>
                        <ButtonStyle variant="contained" sx={{ ml: 2, width: '110px' }} onClick={handleClose}>
                            <FormattedMessage id="add" />
                        </ButtonStyle>
                    </Box>
                </Grid>
            </Box>
            <GlobalDialog
                onClose={() => setOpenPreview(false)}
                open={openPreview}
                Component={<Preview onClose={() => setOpenPreview(false)} itemId={currentId} previewType={currentType} />}
            />
        </>
    );
};

const ModalCloseButton = {
    position: 'absolute',
    top: '15px',
    right: '10px',
    '& svg': {
        width: '1em',
        height: '1em'
    }
};

export default ObjectSelect;

// components
interface DisplayMatchesProps {
    item: ContainerObject;
    term?: string;
}
function DisplayMatches(props: DisplayMatchesProps): JSX.Element | null {
    const { item, term } = props;

    const { matches } = item;
    if (matches === undefined) return null;
    if (term === undefined) return null;

    return (
        <>
            {Object.keys(matches).map(
                (key: string) =>
                    matches[key] && (
                        <React.Fragment key={key}>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>
                                <>
                                    <FormattedMessage id={key} values={{ indicator: '' }} />:
                                </>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '11px' }}>
                                {` ${matches[key]} `}
                            </Typography>
                        </React.Fragment>
                    )
            )}
        </>
    );
}

interface ListItemComponentProps {
    item: any;
    newFilters: Filter[];
    handleOnChange: (e: any, item: any) => void;
}

const ListItemComponent = ({ item, newFilters, handleOnChange }: ListItemComponentProps) => (
    <>
        {Object.keys(item.values).map((key: string) => (
            <>
                {item.values[key].name && (
                    <ListItem key={key}>
                        {item.name === 'examName' && (
                            <Checkbox
                                onChange={(e) => handleOnChange(e, item)}
                                value={key}
                                checked={newFilters.some((f) => f.value === key)}
                            />
                        )}
                        {item.name !== 'examName' && <Checkbox onChange={(e) => handleOnChange(e, item)} value={key} />}
                        <ListItemText primary={<FormattedMessage id={item.values[key].name} defaultMessage={item.values[key].name} />} />
                    </ListItem>
                )}
            </>
        ))}
    </>
);
